import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Save,
  ArrowBack,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { setCredentials } from "../features/counter/Authslice.js";
import configuration from "../config.js";

const FORM_FIELDS = {
  PREFIX: "prefix",
  LOGIN_CODE: "loginCode",
  EMAIL: "email",
  PHONE: "phone",
  USERNAME: "username",
  PERMISSIONS: "permissions",
};

const VALIDATION_RULES = {
  [FORM_FIELDS.PREFIX]: {
    required: "Prefix is required",
    minLength: {
      value: 2,
      message: "Prefix must be at least 2 characters",
    },
  },
  [FORM_FIELDS.USERNAME]: {
    required: "Username is required",
    minLength: {
      value: 3,
      message: "Username must be at least 3 characters",
    },
  },
  [FORM_FIELDS.EMAIL]: {
    required: "Email is required",
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Please enter a valid email address",
    },
  },
  [FORM_FIELDS.PHONE]: {
    required: "Phone number is required",
    pattern: {
      value: /^[0-9]{10}$/,
      message: "Please enter a valid 10-digit phone number",
    },
  },
  [FORM_FIELDS.LOGIN_CODE]: {
    required: "Login code is required",
    minLength: {
      value: 4,
      message: "Login code must be at least 4 characters",
    },
  },
};

const DEFAULT_FORM_VALUES = {
  [FORM_FIELDS.PREFIX]: "",
  [FORM_FIELDS.LOGIN_CODE]: "",
  [FORM_FIELDS.EMAIL]: "",
  [FORM_FIELDS.PHONE]: "",
  [FORM_FIELDS.USERNAME]: "",
  [FORM_FIELDS.PERMISSIONS]: [],
};

const useSubUserUpdate = () => {
  const [showLoginCode, setShowLoginCode] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(false);

  const clearMessages = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  return {
    showLoginCode,
    setShowLoginCode,
    error,
    setError,
    success,
    setSuccess,
    loading,
    setLoading,
    fetchingUser,
    setFetchingUser,
    clearMessages,
  };
};

const useSubUserAPI = (token) => {
  const { API_BASE_URL } = configuration;

  const fetchSubUser = useCallback(
    async (subUserId) => {
      if (!subUserId) {
        throw new Error("Sub-user ID is required");
      }

      const endpoint = `${API_BASE_URL}/api/v1/getSubUserById/${subUserId}`;
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Failed to fetch user data");
      }

      return response.data.data?.subUser || response.data.data;
    },
    [API_BASE_URL, token]
  );

  const updateSubUser = useCallback(
    async (subUserId, updateData) => {
      const endpoint = `${API_BASE_URL}/api/v1/updateSubUserProfileById/${subUserId}/profile`;
      const response = await axios.patch(endpoint, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Update failed");
      }

      return response.data;
    },
    [API_BASE_URL, token]
  );

  return { fetchSubUser, updateSubUser };
};

// Loading component
const LoadingScreen = ({ subUserId }) => (
  <Container
    maxWidth="xs"
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <Box sx={{ textAlign: "center" }}>
      <CircularProgress size={48} />
      <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
        Loading user data...
      </Typography>
      <Typography
        variant="caption"
        sx={{ mt: 1, display: "block", color: "text.secondary" }}
      >
        ID: {subUserId || "Not specified"}
      </Typography>
    </Box>
  </Container>
);

// Error screen component
const ErrorScreen = ({ error, params, location, subUserId, onGoBack }) => (
  <Container
    maxWidth="sm"
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      py: 2,
    }}
  >
    <Paper elevation={3} sx={{ p: 4, textAlign: "center", width: "100%" }}>
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>

      <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
        Debug Information
      </Typography>

      <Box sx={{ textAlign: "left", mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>URL:</strong> {location.pathname}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Parameters:</strong> {JSON.stringify(params, null, 2)}
        </Typography>
        <Typography variant="body2">
          <strong>Sub-User ID:</strong> {subUserId || "undefined"}
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={<ArrowBack />}
        onClick={onGoBack}
        sx={{ px: 4 }}
      >
        Go Back
      </Button>
    </Paper>
  </Container>
);

const FormField = ({
  name,
  control,
  rules,
  label,
  type = "text",
  placeholder,
  endAdornment,
}) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field, fieldState: { error } }) => (
      <TextField
        {...field}
        fullWidth
        label={label}
        type={type}
        variant="outlined"
        margin="normal"
        error={!!error}
        helperText={error?.message}
        placeholder={placeholder}
        InputProps={endAdornment ? { endAdornment } : undefined}
        sx={{
          "& .MuiOutlinedInput-root": {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 2,
            },
          },
        }}
      />
    )}
  />
);

// Main component
const SubUserUpdate = () => {
  const {
    showLoginCode,
    setShowLoginCode,
    error,
    setError,
    success,
    setSuccess,
    loading,
    setLoading,
    fetchingUser,
    setFetchingUser,
    clearMessages,
  } = useSubUserUpdate();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  const { user, token } = useSelector((state) => state.auth);
  const { fetchSubUser, updateSubUser } = useSubUserAPI(token);

  const passedSubUserData = location.state?.subUserData;
  const subUserId = params.subUserId || params.id || params.userId;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
  });

  // Populate form with user data
  const populateForm = useCallback(
    (userData) => {
      const actualUserData = userData.subUser || userData;

      const formData = {
        [FORM_FIELDS.PREFIX]:
          actualUserData.subUserPrefix || actualUserData.prefix || "",
        [FORM_FIELDS.LOGIN_CODE]: actualUserData.loginCode || "",
        [FORM_FIELDS.EMAIL]: actualUserData.email || "",
        [FORM_FIELDS.PHONE]: actualUserData.phone || "",
        [FORM_FIELDS.USERNAME]: actualUserData.username || "",
        [FORM_FIELDS.PERMISSIONS]: actualUserData.permissions || [],
      };

      reset(formData);
      Object.keys(formData).forEach((key) => {
        setValue(key, formData[key]);
      });
    },
    [reset, setValue]
  );

  // Fetch sub-user data
  const handleFetchSubUserData = useCallback(async () => {
    if (!subUserId) {
      setError("Sub-user ID is required");
      return;
    }

    setFetchingUser(true);
    clearMessages();

    try {
      const userData = await fetchSubUser(subUserId);
      populateForm({ subUser: userData });
    } catch (err) {
      console.error("Error fetching sub-user data:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch user data. Please try again."
      );
    } finally {
      setFetchingUser(false);
    }
  }, [
    subUserId,
    fetchSubUser,
    populateForm,
    setError,
    setFetchingUser,
    clearMessages,
  ]);

  // Handle form submission
  const handleSubUserUpdate = useCallback(
    async (formData) => {
      setLoading(true);
      clearMessages();

      try {
        const updateData = {
          subUserPrefix: formData[FORM_FIELDS.PREFIX],
          loginCode: formData[FORM_FIELDS.LOGIN_CODE],
          email: formData[FORM_FIELDS.EMAIL],
          phone: formData[FORM_FIELDS.PHONE],
          username: formData[FORM_FIELDS.USERNAME],
          permissions: formData[FORM_FIELDS.PERMISSIONS],
        };

        const response = await updateSubUser(subUserId, updateData);

        setSuccess("Profile updated successfully!");

        if (user?._id === subUserId) {
          const updatedUser = { ...user, ...response.data };
          dispatch(
            setCredentials({ token, user: updatedUser, isAuthenticated: true })
          );
        }

        // Refresh data
        await handleFetchSubUserData();
      } catch (err) {
        console.error("Sub-user update error:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to update profile. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [
      subUserId,
      updateSubUser,
      user,
      token,
      dispatch,
      handleFetchSubUserData,
      setLoading,
      setSuccess,
      setError,
      clearMessages,
    ]
  );

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const togglePasswordVisibility = useCallback(() => {
    setShowLoginCode(!showLoginCode);
  }, [showLoginCode]);

  useEffect(() => {
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      return;
    }

    if (!configuration.API_BASE_URL) {
      setError("API configuration not found.");
      return;
    }

    if (passedSubUserData) {
      populateForm(passedSubUserData);
      return;
    }

    if (subUserId) {
      handleFetchSubUserData();
    } else {
      setError("Sub-user ID is required.");
    }
  }, [
    subUserId,
    token,
    passedSubUserData,
    populateForm,
    handleFetchSubUserData,
    setError,
  ]);

  if (fetchingUser) {
    return <LoadingScreen subUserId={subUserId} />;
  }

  if (error && !passedSubUserData) {
    return (
      <ErrorScreen
        error={error}
        params={params}
        location={location}
        subUserId={subUserId}
        onGoBack={handleGoBack}
      />
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Sub-User Profile
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Update your profile information below
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {passedSubUserData && (
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 0.5,
                  backgroundColor: "info.main",
                  color: "white",
                  borderRadius: 1,
                }}
              >
                ✓ Data Loaded
              </Typography>
            )}
          </Box>
        </Box>

        <Fade in={!!error}>
          <Box sx={{ mb: error ? 2 : 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </Fade>

        <Fade in={!!success}>
          <Box sx={{ mb: success ? 2 : 0 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
          </Box>
        </Fade>

        <form onSubmit={handleSubmit(handleSubUserUpdate)}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormField
                name={FORM_FIELDS.PREFIX}
                control={control}
                rules={VALIDATION_RULES[FORM_FIELDS.PREFIX]}
                label="Sub-User Prefix"
                placeholder="e.g., SUB001"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormField
                name={FORM_FIELDS.USERNAME}
                control={control}
                rules={VALIDATION_RULES[FORM_FIELDS.USERNAME]}
                label="Username"
                placeholder="Enter username"
              />
            </Grid>

            <Grid item xs={12}>
              <FormField
                name={FORM_FIELDS.EMAIL}
                control={control}
                rules={VALIDATION_RULES[FORM_FIELDS.EMAIL]}
                label="Email Address"
                type="email"
                placeholder="Enter email address"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormField
                name={FORM_FIELDS.PHONE}
                control={control}
                rules={VALIDATION_RULES[FORM_FIELDS.PHONE]}
                label="Phone Number"
                placeholder="Enter 10-digit phone number"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormField
                name={FORM_FIELDS.LOGIN_CODE}
                control={control}
                rules={VALIDATION_RULES[FORM_FIELDS.LOGIN_CODE]}
                label="Login Code"
                type={showLoginCode ? "text" : "password"}
                placeholder="Enter login code"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      aria-label={
                        showLoginCode ? "Hide login code" : "Show login code"
                      }
                    >
                      {showLoginCode ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: "success.main",
                "&:hover": {
                  backgroundColor: "success.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabledBackground",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Updating...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>

            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              disabled={loading}
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Go Back
            </Button>
          </Box>
        </form>

        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Profile Management System
            </Typography>
          </Divider>
          <Typography
            variant="body2"
            sx={{ textAlign: "center" }}
            color="text.secondary"
          >
            Need assistance? Contact your administrator or support team for
            help.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SubUserUpdate;
